import React, { useState, useEffect, useCallback } from "react";
import { Spinner, Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  Layers, ListChecks, Trophy, Clock, HelpCircle, Lock, CheckCircle2,
  ArrowLeft, BarChart3,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { EmptyState, PageHeader } from "../ui";
import "./StudentTestSeries.scss";

const STATUS_LABELS = {
  available: "Available", in_progress: "In Progress", completed: "Completed",
  expired: "Expired", upcoming: "Upcoming", locked: "Locked",
};

const StudentTestSeriesDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("tests");
  const [board, setBoard] = useState(null);
  const [boardLoading, setBoardLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/student-test-series/${id}`);
      setSeries(data.series);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadBoard = useCallback(async () => {
    if (board) return;
    setBoardLoading(true);
    try {
      const { data } = await apiClient.get(`/student-test-series/${id}/leaderboard`);
      setBoard(data);
    } catch { /* silent */ } finally {
      setBoardLoading(false);
    }
  }, [id, board]);

  useEffect(() => { if (tab === "leaderboard") loadBoard(); }, [tab, loadBoard]);

  const goToTest = (t) => {
    // Remember the series so the result page can offer "Back to Test Series".
    sessionStorage.setItem("vc-active-series", id);
    if (t.studentStatus === "in_progress" && t.inProgressAttemptId) {
      navigate(`/s/assessment/take/${t.inProgressAttemptId}`);
    } else {
      navigate(`/s/assessment/${t.assessmentId}`);
    }
  };

  const actionLabel = (t) => {
    switch (t.studentStatus) {
      case "in_progress": return "Continue";
      case "completed": return "Review";
      case "available": return "Start";
      case "locked": return "Locked";
      default: return "View";
    }
  };

  if (loading) return <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>;
  if (!series) return <EmptyState icon={<Layers size={40} />} title="Series not found" />;

  return (
    <div className="student-test-series-detail">
      <PageHeader title={series.title} description={series.description} icon={Layers}
        backButton onBack={() => navigate("/s/test-series")} />

      {/* Progress + standing */}
      <div className="sts-overview">
        <div className="sts-ring" style={{ "--pct": series.progressPct || 0 }}>
          <span>{series.progressPct || 0}%</span>
        </div>
        <div className="sts-overview__stats">
          <div className="sts-stat"><ListChecks size={15} /> {series.completedTests}/{series.totalTests} tests completed</div>
          {series.leaderboardEnabled && series.myStanding && (
            <>
              <div className="sts-stat"><Trophy size={15} /> Rank <strong>#{series.myStanding.rank}</strong> of {series.enrolled}</div>
              <div className="sts-stat"><BarChart3 size={15} /> {series.myStanding.percentile}th percentile</div>
            </>
          )}
          {series.leaderboardEnabled && !series.myStanding && (
            <div className="sts-stat sts-muted">Complete a test to enter the leaderboard.</div>
          )}
        </div>
      </div>

      {series.instructions && (
        <div className="sts-instructions">{series.instructions}</div>
      )}

      {/* Tabs */}
      <div className="sts-tabs">
        <button type="button" className={`sts-tab${tab === "tests" ? " sts-tab--active" : ""}`} onClick={() => setTab("tests")}>
          <ListChecks size={15} /> Tests
        </button>
        {series.leaderboardEnabled && (
          <button type="button" className={`sts-tab${tab === "leaderboard" ? " sts-tab--active" : ""}`} onClick={() => setTab("leaderboard")}>
            <Trophy size={15} /> Leaderboard
          </button>
        )}
      </div>

      {tab === "tests" && (
        <div className="sts-tests">
          {(series.tests || []).map((t, idx) => (
            <div key={t.assessmentId} className={`sts-test${t.locked ? " sts-test--locked" : ""}`}>
              <span className="sts-test__num">{idx + 1}</span>
              <div className="sts-test__body">
                <div className="sts-test__title">
                  {t.label || t.title}
                  {t.isOptional && <span className="sts-badge sts-badge--muted">Optional</span>}
                </div>
                <div className="sts-test__meta">
                  <span className={`sts-badge sts-badge--${t.studentStatus}`}>{STATUS_LABELS[t.studentStatus]}</span>
                  <span><HelpCircle size={13} /> {t.questionCount} Q</span>
                  {t.timeLimit > 0 && <span><Clock size={13} /> {t.timeLimit} min</span>}
                  {t.completed && <span><CheckCircle2 size={13} /> Best: {t.bestScore}%</span>}
                  {t.locked && t.lockReason && <span className="sts-lock"><Lock size={13} /> {t.lockReason}</span>}
                </div>
              </div>
              <Button size="sm"
                variant={t.studentStatus === "in_progress" ? "warning" : t.studentStatus === "available" ? "primary" : "outline-secondary"}
                disabled={t.locked || t.studentStatus === "upcoming" || t.studentStatus === "expired"}
                onClick={() => goToTest(t)}>
                {actionLabel(t)}
              </Button>
            </div>
          ))}
        </div>
      )}

      {tab === "leaderboard" && (
        boardLoading ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
        ) : !board || !board.enabled || (board.leaderboard || []).length === 0 ? (
          <EmptyState icon={<Trophy size={36} />} title="No rankings yet"
            description="The leaderboard fills in as students complete tests." />
        ) : (
          <table className="sts-leaderboard">
            <thead>
              <tr><th>Rank</th><th>Student</th><th>Tests</th><th>Score</th><th>Avg %</th><th>%ile</th></tr>
            </thead>
            <tbody>
              {board.leaderboard.map((r) => {
                const isMe = String(r.studentId) === String(board.myStudentId);
                return (
                  <tr key={r.studentId} className={isMe ? "sts-me" : ""}>
                    <td><span className={`sts-rank sts-rank-${r.rank}`}>#{r.rank}</span></td>
                    <td>{r.name}{isMe && <span className="sts-badge sts-badge--muted ms-1">You</span>}</td>
                    <td>{r.testsCompleted}</td>
                    <td>{r.totalScore}</td>
                    <td>{r.avgPercentage}%</td>
                    <td>{r.percentile}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )
      )}

      <div className="mt-3">
        <Button variant="link" size="sm" onClick={() => navigate("/s/test-series")}>
          <ArrowLeft size={14} /> All test series
        </Button>
      </div>
    </div>
  );
};

export default StudentTestSeriesDetailPage;
